import { logError } from '@/lib/log-error';
import { useQueryClient } from '@tanstack/react-query';
import type { ChatRequestOptions, ChatStatus, UIMessage } from 'ai';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { conversationQueryOptions } from '../api/use-conversation';
import { conversationStartErrorCode } from './conversation-start-error';
import { convertStoredToUIMessages } from './convert-messages';

export function useChatRetry({
  conversationId,
  messages,
  status,
  error,
  setMessages,
  regenerate,
  requestBody,
}: {
  conversationId?: string;
  messages: UIMessage[];
  status: ChatStatus;
  error?: Error;
  setMessages: (
    next: UIMessage[] | ((current: UIMessage[]) => UIMessage[])
  ) => void;
  regenerate: (options?: ChatRequestOptions) => Promise<void>;
  requestBody: { current: Record<string, unknown> | undefined };
}) {
  const queryClient = useQueryClient();
  const mounted = useRef(true);
  const pending = useRef(false);
  const latest = useRef({ messages, status, error });
  latest.current = { messages, status, error };
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return useCallback(async () => {
    if (pending.current || status === 'submitted' || status === 'streaming')
      return;
    pending.current = true;
    try {
      const body = requestBody.current;
      if (conversationStartErrorCode(error?.message ?? null)) {
        const unsent = messages.at(-1);
        if (
          !conversationId ||
          body?.conversationId !== conversationId ||
          status !== 'error' ||
          unsent?.role !== 'user'
        )
          return;
        // An explicit retry must include the other turn that may have finished
        // while this view held a rejected optimistic user message.
        const saved = await queryClient.fetchQuery({
          ...conversationQueryOptions(conversationId),
          staleTime: 0,
        });
        if (
          !mounted.current ||
          latest.current.status !== 'error' ||
          latest.current.error !== error ||
          latest.current.messages !== messages ||
          saved.id !== conversationId
        )
          return;
        const persisted = convertStoredToUIMessages(saved.messages);
        if (persisted.some((message) => message.id === unsent.id)) return;
        let applied = false;
        setMessages((current) => {
          if (current !== messages) return current;
          applied = true;
          return [...persisted, unsent];
        });
        if (!applied) return;
      }
      // Never replay automatically. This callback only belongs to Retry.
      await regenerate({ body });
    } catch (retryError) {
      logError('assistant.retry', retryError, { feature: 'assistant' });
      if (mounted.current)
        toast.error(
          'Could not reload the conversation. Please retry in a moment.'
        );
    } finally {
      pending.current = false;
    }
  }, [
    conversationId,
    messages,
    status,
    error,
    requestBody,
    queryClient,
    setMessages,
    regenerate,
  ]);
}
